import { DayPicker, type DayButtonProps } from '@daypicker/react';
import { useEffect, useId, useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { dayIn, dayLocale, showDate, toDate, toIso, type IsoDate } from './dates';
import { CalendarIcon } from './icons';
import { Popover } from './Popover';

export interface DatePickProps {
  label: string;
  value: IsoDate | null;
  onChange: (value: IsoDate | null) => void;
  placeholder?: string;
  /** It must be filled in: its label is marked with a star. */
  required?: boolean;
  /** The first day that may be picked: a trip cannot start in the past. */
  from?: IsoDate;
  /** What a day is in the trip, under its number: where it is spent, a way taken that day. */
  note?: ((day: IsoDate) => DayNote | null) | undefined;
}

/**
 * A text given to a date field from outside it, as a PDF's word pressed is: sent to its button,
 * `taken` set once the date it says is picked.
 */
export const TAKE_TEXT = 'trippath:take-text';

export interface TakenText {
  text: string;
  taken: boolean;
}

/** A day of the trip as the calendar says it: a short mark under its number, said in full. */
export interface DayNote {
  /** Under the number: a country's code, `AU`. */
  mark: string;
  /** In full, when pointed at and to a screen reader: `Australia · Sydney`. */
  said: string;
  /** A drawing beside the mark: a way taken that day. */
  icon?: ReactNode;
}

/** A day's button with its note under its number, focused when the calendar moves to it. */
function NotedDay({
  day,
  modifiers,
  note,
  ...button
}: DayButtonProps & { note: (day: IsoDate) => DayNote | null }) {
  const said = note(toIso(day.date));
  return (
    <button
      ref={(element) => {
        if (element && modifiers.focused) element.focus();
      }}
      {...button}
      title={said?.said}
      aria-label={said ? `${String(button['aria-label'])}, ${said.said}` : button['aria-label']}
    >
      {button.children}
      {said && (
        <span className="rdp-note" aria-hidden="true">
          {said.icon}
          {said.mark}
        </span>
      )}
    </button>
  );
}

/** One date, picked from a calendar that starts on Monday — never `<input type="date">`. */
export function DatePick({
  label,
  value,
  onChange,
  placeholder,
  from,
  required = false,
  note,
}: DatePickProps) {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const id = useId();
  // Its own box, once laid out, to hear a text given to it.
  const [field, setField] = useState<HTMLDivElement | null>(null);
  const date = value === null ? undefined : toDate(value);
  const earliest = from === undefined ? undefined : toDate(from);
  const shown = date ? showDate(date, i18n.language) : (placeholder ?? t('date.placeholder'));
  // Each day's note under its number, when the calendar is told the trip's days.
  const noted = useMemo(
    () =>
      note === undefined
        ? {}
        : {
            className: 'rdp-noted',
            components: {
              DayButton: (props: DayButtonProps) => <NotedDay {...props} note={note} />,
            },
          },
    [note],
  );

  // A text given to it: the date it says, the year nearest the one it has, else the first allowed.
  useEffect(() => {
    if (field === null) return;
    const take = (event: Event) => {
      const given = (event as CustomEvent<TakenText>).detail;
      const day = dayIn(given.text, value ?? from ?? toIso(new Date()));
      if (day === null) return;
      onChange(day);
      given.taken = true;
    };
    field.addEventListener(TAKE_TEXT, take);
    return () => {
      field.removeEventListener(TAKE_TEXT, take);
    };
  }, [field, value, from, onChange]);

  return (
    <div className="field" ref={setField}>
      <span className="field-label" id={`${id}-label`} data-required={required || undefined}>
        {label}
      </span>
      <Popover
        open={open}
        onOpenChange={setOpen}
        label={label}
        trigger={(props) => (
          <button
            type="button"
            className="pick-trigger"
            data-takes-text="day"
            aria-labelledby={`${id}-label ${id}-value`}
            {...props}
          >
            <span id={`${id}-value`} className={date ? undefined : 'pick-placeholder'}>
              {shown}
            </span>
            <CalendarIcon />
          </button>
        )}
      >
        <DayPicker
          mode="single"
          selected={date}
          defaultMonth={date ?? new Date()}
          weekStartsOn={1}
          disabled={earliest === undefined ? undefined : { before: earliest }}
          locale={dayLocale(i18n.language)}
          {...noted}
          autoFocus
          onSelect={(picked) => {
            onChange(picked ? toIso(picked) : null);
            setOpen(false);
          }}
        />
      </Popover>
    </div>
  );
}
